'use client';

import Button from '@/components/Button';
import Dialog from '@/components/Dialog';
import Input from '@/components/Form/FormInput';
import Select from '@/components/Form/Select';
import Image from '@/components/Image';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { PlusOutlined } from '@zeelool/icon';

import { addCollaboration } from '@/api/public';
import Popup from '@/components/Popup';
import { useStore } from '@/providers/app-store-provider';
import { Store } from '@/store';
import { IS_DE } from '@/utils/constants';
import { env } from '@/utils/env';
import { Box, MenuItem, useMediaQuery } from '@mui/material';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { useTranslations } from 'next-intl';
import React, { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// 图片资源路径常量（暂未迁移到 public 目录，后续可优化）
const STATIC_BASE = '/static-page/influencer-program';

interface SocialInfo {
  id: number;
  social: string;
  name: string;
  link: string;
  followers: string;
  followersCountry: string;
}

const socialPlatforms = ['TikTok', 'Instagram', 'YouTube', 'Facebook'];
const followerRanges = ['0-10K', '10K-100K', '100K-500K', '500K-1M', '1M+'];

export default function InfluencerProgramClient() {
  const t = useTranslations('influencer-program');
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [showForm, setShowForm] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [progress, setProgress] = useState(0); // 进度值（0-100）
  const { countryList } = useStore<Store>();
  const publicRelationEmail = env.NEXT_PUBLIC_PUBLIC_RELATION_EMAIL || 'publicrelation@zeelool.com';
  interface FormValues {
    country: string;
    email: string;
    socialList: SocialInfo[];
  }

  const form = useForm<FormValues>({
    defaultValues: {
      country: '',
      email: '',
      socialList: [
        {
          id: 1,
          social: '',
          name: '',
          link: '',
          followers: '',
          followersCountry: '',
        },
      ],
    },
    mode: 'all',
  });
  const { control, handleSubmit: rhfHandleSubmit, reset } = form;
  const { fields, append, remove } = useFieldArray({ control, name: 'socialList' });

  /** banner、轮播图等静态资源 */
  const imgList = [
    {
      name: '@msstylengrace Xion',
      picture: '/static-page/influencer-program/1.png',
    },
    {
      name: '@starbuxx',
      picture: '/static-page/influencer-program/2.png',
    },
    {
      name: '@dree.o',
      picture: '/static-page/influencer-program/3.png',
    },
    {
      name: '@jadee__marie',
      picture: '/static-page/influencer-program/4.png',
    },
    {
      name: '@istina.manners',
      picture: '/static-page/influencer-program/5.png',
    },
    {
      name: '@jennifer_pixie',
      picture: '/static-page/influencer-program/6.png',
    },
    {
      name: '@annalunamakeup',
      picture: '/static-page/influencer-program/7.png',
    },
    {
      name: '@oh.doubleyou',
      picture: '/static-page/influencer-program/8.png',
    },
    {
      name: '@gelseyalana',
      picture: '/static-page/influencer-program/9.png',
    },
    {
      name: '@jonica',
      picture: '/static-page/influencer-program/10.png',
    },
    {
      name: '@dree.o',
      picture: '/static-page/influencer-program/11.png',
    },
    {
      name: '@cassie_rxx',
      picture: '/static-page/influencer-program/12.png',
    },
    {
      name: '@ohathena',
      picture: '/static-page/influencer-program/13.png',
    },
    {
      name: '@starbuxx',
      picture: '/static-page/influencer-program/14.png',
    },
    {
      name: '@dejajanea',
      picture: '/static-page/influencer-program/15.png',
    },
  ];

  const handleAddSocial = () => {
    append({
      id: Date.now(),
      social: '',
      name: '',
      link: '',
      followers: '',
      followersCountry: '',
    });
  };

  const handleRemoveSocial = (index: number) => {
    if (index === 0) return;
    remove(index);
  };

  // 计算进度条在三个区间中的位置
  const calculateProgressPosition = (progressValue: number) => {
    // 将100%分为三个区间：0-33%, 33-66%, 66-100%
    const segment = Math.floor(progressValue / 33.33);
    const segmentProgress = (progressValue % 33.33) / 33.33;

    // 计算在当前区间内的位置（0-33.33%的范围内）
    const positionInSegment = segmentProgress * 33.33;

    // 计算总的left位置
    const leftPosition = segment * 33.33 + positionInSegment;

    return Math.min(leftPosition, 66.67); // 最大不超过66.67%（因为滑块本身占33.33%宽度）
  };

  // 处理幻灯片变化
  const handleSlideChange = (swiper: any) => {
    const { realIndex } = swiper; // 获取真实的幻灯片索引（不包括克隆的）
    // 根据索引计算进度
    const _progress = (realIndex / (imgList.length - 1)) * 100;
    setProgress(_progress);
  };

  const onSubmit = async (values: FormValues) => {
    const payload = {
      country: values.country,
      influencer_info: JSON.stringify(
        values.socialList.map((item) => ({
          social_platform: item.social,
          account_name: item.name,
          followers: item.followers,
          top_location_of_followers: item.followersCountry,
          link_to_social_platform: item.link,
        })),
      ),
      business_email: values.email,
    };

    try {
      setBtnLoading(true);
      await addCollaboration(payload);
      setShowForm(false);
      reset({
        country: '',
        email: '',
        socialList: [
          { id: 1, social: '', name: '', link: '', followers: '', followersCountry: '' },
        ],
      });
    } finally {
      setBtnLoading(false);
    }
  };

  return (
    <div className="influencer-program text-black bg-[#ffffff] w-full overflow-hidden">
      {/* 顶部 Banner */}
      <section className="top-banner cursor-pointer" onClick={() => setShowForm(true)}>
        <Image
          src={
            isMobile
              ? `${STATIC_BASE}/banner_mobile${IS_DE ? '.de.jpg' : '.png'}`
              : `${STATIC_BASE}/banner${IS_DE ? '.de.jpg' : '.png'}`
          }
          alt={t('bannerAlt')}
          width={1920}
          height={600}
          className="w-full h-auto"
        />
      </section>

      {/* WHO ARE WE LOOKING FOR */}
      <section className="section-looking py-40 pl-20 md:py-[120px] md:flex md:items-center md:justify-center px-4 md:px-0 font-bb">
        {isMobile && (
          <p className="section-title font-bs text-[21px] md:text-[38px] mb-[30px] text-center">
            {t('whoAreWeLookingFor')}
          </p>
        )}
        <div className="flex md:max-w-[1200px] md:gap-x-40 w-full">
          <div className="text-center md:text-left mb-6 md:mb-0">
            {!isMobile && (
              <p className="section-title font-bs text-[21px] md:text-[38px] mb-[60px]">
                {t('whoAreWeLookingFor')}
              </p>
            )}
            <div className="flex flex-col items-center md:items-start">
              <div
                className={` w-[196px] md:w-[440px] h-[58px] md:h-[64px] mb-[18px] md:mb-[30px]  flex items-center justify-center text-[13px] md:text-[22px] md:self-end translate-x-[20px] md:translate-x-[30px] bg-no-repeat bg-[length:100%_100%] bg-[url('/zeelool-next/static-page/influencer-program/looking_bg.png')]`}
              >
                {t('fashionPrescriptionEyewearWearers')}
              </div>
              <div
                className={`w-[196px] md:w-[440px] h-[38px] md:h-[64px] mb-[18px] md:mb-[30px] flex items-center justify-center text-[13px] md:text-[22px] bg-no-repeat bg-[length:100%_100%] bg-[url('/zeelool-next/static-page/influencer-program/looking_bg.png')]`}
              >
                {t('trendyBeautyBabes')}
              </div>
              <div
                className={` w-[196px] md:w-[440px] h-[38px] md:h-[64px] mb-[18px] md:mb-[30px] flex items-center justify-center text-[13px] md:text-[22px] md:self-end translate-x-[20px] md:translate-x-[30px] bg-no-repeat bg-[length:100%_100%] bg-[url('/zeelool-next/static-page/influencer-program/looking_bg.png')]`}
              >
                {t('makeupArtistAndLovers')}
              </div>
              <div
                className={` w-[196px] md:w-[440px] h-[38px] md:h-[64px]  flex items-center justify-center text-[13px] md:text-[22px] bg-no-repeat bg-[length:100%_100%] bg-[url('/zeelool-next/static-page/influencer-program/looking_bg.png')]`}
              >
                {t('creativeContentCreator')}
              </div>
            </div>
          </div>
          <div className="right-box w-[140px] md:w-[600px] mx-auto md:ml-0">
            <Image
              src={isMobile ? `${STATIC_BASE}/looking_mobile.png` : `${STATIC_BASE}/looking.png`}
              alt={t('lookingImageAlt')}
              width={600}
              height={600}
              className="w-full h-auto"
            />
          </div>
        </div>
      </section>

      {/* PERKS */}
      <section className="section-perks flex flex-col items-center bg-[#f5f6f7] py-10 md:py-[100px]">
        <p className="section-title font-bs text-[21px] md:text-[2.4vw] mb-8 md:mb-[110px]">
          {t('aPeekAtThePerks')}
        </p>
        <div className="flex flex-row justify-around items-start gap-x-[22px] md:gap-x-[18vw] gap-y-10 md:gap-y-0 mt-18 mb-28">
          <div className="perks-item flex flex-col items-center">
            <Image
              src={`${STATIC_BASE}/boxes.png`}
              alt={t('prGiftBoxesAlt')}
              width={210}
              height={210}
              className="w-[65px] md:w-[210px] mb-2 md:mb-[55px]"
            />
            <span className="text-[13px] md:text-[1.875vw] font-bs text-center mt-8">
              {t('prGiftBoxes')}
            </span>
          </div>
          <div className="perks-item flex flex-col items-center">
            <Image
              src={`${STATIC_BASE}/bonuses.png`}
              alt={t('bonusesAlt')}
              width={210}
              height={210}
              className="w-[65px] md:w-[210px] mb-2 md:mb-[55px]"
            />
            <span className="text-[13px] md:text-[1.875vw] font-bs text-center mt-8">
              {t('bonuses')}
            </span>
          </div>
          <div className="perks-item flex flex-col items-center">
            <Image
              src={`${STATIC_BASE}/community.png`}
              alt={t('exposureAlt')}
              width={210}
              height={210}
              className="w-[65px] md:w-[210px] mb-2 md:mb-[55px]"
            />
            <span className="text-[13px] md:text-[1.875vw] font-bs text-center mt-8">
              {t('exposureToOurCommunity')}
            </span>
          </div>
        </div>
      </section>

      {/* JOIN US */}
      <section className="section-join flex flex-col md:flex-row items-center justify-center py-40 md:py-[154px] px-16 md:px-0">
        <div className="left-box w-[285px] md:w-[680px] mb-8 md:mb-0">
          <Image
            src={`${STATIC_BASE}/join.png`}
            alt={t('joinImageAlt')}
            width={680}
            height={680}
            className="w-full h-auto"
          />
        </div>
        <div className="right-box md:ml-[110px] text-center md:text-left max-w-[667px]">
          <p className="section-title font-bs text-[21px] mt-12 md:mt-0 md:text-[2.4vw] mb-4 text-center">
            {t('joinUs')}
          </p>
          <p className="text-[18px] md:text-[1.66vw] leading-[22px] md:leading-[48px] font-br text-left">
            {t('joinDescription')}
            <br />
            {t.rich('joinDescription2', {
              publicRelationEmail,
              email: (chunks) => (
                <a href={`mailto:${publicRelationEmail}`} className="text-primary-purple underline">
                  {chunks}
                </a>
              ),
            })}
          </p>
          <div
            className={`mx-auto w-[250px] md:w-[500px] h-[71px] md:h-[120px] mt-[32px] md:mt-[90px] bg-cover bg-center bg-no-repeat flex items-center justify-center text-[20px] md:text-[1.56vw] font-bb cursor-pointer ${
              isMobile
                ? `bg-[url('/zeelool-next/static-page/influencer-program/button_mobile.png')]`
                : `bg-[url('/zeelool-next/static-page/influencer-program/button.png')]`
            }`}
            onClick={() => setShowForm(true)}
          >
            <div className=" flex items-center justify-center mt-[-30px] md:mt-[-30px]">
              {t('joinNow')}
            </div>
          </div>
        </div>
      </section>

      {/* INFLUENCER FAM */}
      <section className="section-influencer bg-[#f5f6f7] py-10 md:py-[100px] px-4 md:px-[50px] mb-[60px] md:mb-[130px]">
        <p className="section-title font-bs text-[21px] md:text-[2.4vw] text-center mb-24 md:mb-[60px] ">
          {t('getToKnowOurInfluencerFam')}
        </p>
        <Swiper
          className="img-swiper w-full"
          modules={[Autoplay, Pagination]}
          pagination={{ el: '.swiper-pagination', clickable: true }}
          autoplay={{ delay: 0, disableOnInteraction: false, waitForTransition: true }}
          loop
          speed={2000}
          slidesPerView={3}
          spaceBetween={3}
          breakpoints={{
            768: { slidesPerView: 5, spaceBetween: 30 },
          }}
          onSlideChange={handleSlideChange}
        >
          {imgList.map((item, idx) => (
            <SwiperSlide key={idx} className="img-item">
              <Image
                src={item.picture}
                alt={t('influencerImageAlt')}
                width={210}
                height={210}
                className="w-full h-auto"
              />
              <p className="mt-4 md:mt-24 text-center text-[14px] md:text-[16px] leading-20">
                {item.name}
              </p>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* 进度条 */}
        <div className="mt-24 md:mt-32 mx-auto flex justify-center">
          <div className="w-[300px] bg-gray-300 h-8 overflow-hidden rounded-2xl relative">
            <div
              className="h-full w-1/3 bg-[#4540e2] rounded-2xl absolute top-0 transition-all duration-300 ease-out"
              style={{
                left: `${calculateProgressPosition(progress)}%`,
              }}
            />
          </div>
        </div>
      </section>

      <WebOnly>
        {/* 表单 Dialog（公共组件 + react-hook-form） */}
        <Dialog
          open={showForm}
          onClose={() => setShowForm(false)}
          fullScreen={isMobile}
          title={<span className="font-bb">{t('dialogTitle')}</span>}
          sx={{
            '& .MuiDialog-paper': {
              width: '100%',
              maxWidth: isMobile ? 'none' : '800px',
              padding: '0px',
              ...(!isMobile && {
                height: '650px',
              }),
            },
          }}
          slots={{
            transition: Transition,
          }}
          footer={
            <div className="p-12 w-4/5 mx-auto">
              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={btnLoading}
                onClick={rhfHandleSubmit(onSubmit)}
              >
                {btnLoading ? t('submitting') : t('submit')}
              </Button>
            </div>
          }
        >
          <Box component="form" className="space-y-6">
            <div className="font-bb text-[20px] mb-18">*{t('question01')} </div>
            <Select
              control={control}
              name="country"
              layout="vertical"
              placeholder={t('pleaseSelect')}
              rules={{ required: t('pleaseSelect') }}
              fullWidth
            >
              {countryList.map(({ country, country_id }) => {
                return (
                  <MenuItem key={country} value={country_id}>
                    {country}
                  </MenuItem>
                );
              })}
            </Select>

            <div className="font-bb text-[20px] mb-18">{t('question02')} </div>
            {fields.map((item, idx) => (
              <Box key={item.id} className="border-b border-gray-300 mb-24 relative space-y-4">
                {idx !== 0 && (
                  <div
                    onClick={() => handleRemoveSocial(idx)}
                    className={'absolute top-0 right-0 text-primary-purple cursor-pointer z-999'}
                  >
                    {t('removeSocialPlatforms')}
                  </div>
                )}

                <Select
                  control={control}
                  name={`socialList.${idx}.social`}
                  label={`* ${t('socialPlatform')}`}
                  layout="vertical"
                  placeholder={t('pleaseSelect')}
                  rules={{ required: t('pleaseSelectASocialPlatform') }}
                  fullWidth
                >
                  {socialPlatforms.map((p) => (
                    <MenuItem key={p} value={p}>
                      {p}
                    </MenuItem>
                  ))}
                </Select>

                <Input
                  control={control}
                  name={`socialList.${idx}.name`}
                  label={`* ${t('socialMediaHandle')}`}
                  layout="vertical"
                  rules={{ required: t('pleaseInputYourSocialMediaHandle') }}
                  fullWidth
                />

                <Input
                  control={control}
                  name={`socialList.${idx}.link`}
                  label={`* ${t('linkToSocialPlatform')}`}
                  layout="vertical"
                  fullWidth
                />

                <Select
                  control={control}
                  name={`socialList.${idx}.followers`}
                  label={`* ${t('followers')}`}
                  layout="vertical"
                  placeholder={t('pleaseSelect')}
                  rules={{ required: t('pleaseSelectFollowersRange') }}
                  fullWidth
                >
                  {followerRanges.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </Select>

                <Input
                  control={control}
                  name={`socialList.${idx}.followersCountry`}
                  label={`* ${t('topLocationOfYourFollowers')}`}
                  layout="vertical"
                  rules={{ required: t('pleaseInputTopLocationOfYourFollowers') }}
                  fullWidth
                />
              </Box>
            ))}
            <Button variant="outlined" startIcon={<PlusOutlined />} onClick={handleAddSocial}>
              {t('addSocialPlatforms')}
            </Button>

            <div className="font-bb text-lg mt-24">{t('question03')}</div>
            <Input
              control={control}
              name="email"
              label={t('businessEmail')}
              rules={{
                pattern: {
                  value: /[^\s@]+@[^\s@]+\.[^\s@]+/,
                  message: t('pleaseInputAValidEmail'),
                },
              }}
              fullWidth
            />
          </Box>
        </Dialog>
      </WebOnly>

      <WapOnly>
        <Popup
          open={showForm}
          onClose={() => setShowForm(false)}
          height="100vh"
          title={t('dialogTitle')}
          sx={{
            borderRadius: 0,
            '& .MuiDrawer-paper': {
              borderRadius: 0,
            },
          }}
        >
          <Box component="form" className="space-y-6">
            <div className="font-bb text-[14px]">*{t('question01')} </div>
            <Input
              control={control}
              name="country"
              label={`${t('country')} *`}
              rules={{ required: t('pleaseFillInYourCountry') }}
              fullWidth
            />

            <div className="font-bb text-[14px]">{t('question02')} </div>
            {fields.map((item, idx) => (
              <Box key={item.id} className="border-b border-gray-300 mb-24 relative space-y-4">
                {idx !== 0 && (
                  <div className="flex justify-end">
                    <div
                      onClick={() => handleRemoveSocial(idx)}
                      className={
                        'inline-flex top-0 right-0 text-primary-purple cursor-pointer z-999 text-[14px]'
                      }
                    >
                      {t('removeSocialPlatforms')}
                    </div>
                  </div>
                )}

                <Select
                  control={control}
                  name={`socialList.${idx}.social`}
                  label={`* ${t('socialPlatform')}`}
                  layout="vertical"
                  placeholder={t('pleaseSelect')}
                  rules={{ required: t('pleaseSelectASocialPlatform') }}
                  fullWidth
                >
                  {socialPlatforms.map((p) => (
                    <MenuItem key={p} value={p}>
                      {p}
                    </MenuItem>
                  ))}
                </Select>

                <Input
                  control={control}
                  name={`socialList.${idx}.name`}
                  label={`* ${t('socialMediaHandle')}`}
                  layout="vertical"
                  rules={{ required: t('pleaseInputYourSocialMediaHandle') }}
                  fullWidth
                />

                <Input
                  control={control}
                  name={`socialList.${idx}.link`}
                  label={`* ${t('linkToSocialPlatform')}`}
                  layout="vertical"
                  rules={{ required: t('pleaseInputTheLinkToSocialPlatform') }}
                  fullWidth
                />

                <Select
                  control={control}
                  name={`socialList.${idx}.followers`}
                  label={`* ${t('followers')}`}
                  layout="vertical"
                  placeholder={t('pleaseSelect')}
                  rules={{ required: t('pleaseSelectFollowersRange') }}
                  fullWidth
                >
                  {followerRanges.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </Select>

                <Input
                  control={control}
                  name={`socialList.${idx}.followersCountry`}
                  label={`* ${t('topLocationOfYourFollowers')}`}
                  layout="vertical"
                  rules={{ required: t('pleaseInputTopLocationOfYourFollowers') }}
                  fullWidth
                />
              </Box>
            ))}
            <Button variant="outlined" startIcon={<PlusOutlined />} onClick={handleAddSocial}>
              {t('addSocialPlatforms')}
            </Button>

            <div className="font-bb text-lg mt-24">{t('question03')}</div>
            <Input
              control={control}
              name="email"
              label={t('businessEmail')}
              rules={{
                pattern: {
                  value: /[^\s@]+@[^\s@]+\.[^\s@]+/,
                  message: t('pleaseInputAValidEmail'),
                },
              }}
              fullWidth
            />
          </Box>

          <div className="w-full mx-auto">
            <Button
              variant="contained"
              color="primary"
              fullWidth
              disabled={btnLoading}
              onClick={rhfHandleSubmit(onSubmit)}
              sx={{
                borderRadius: 0,
              }}
            >
              {btnLoading ? t('submitting') : t('submit')}
            </Button>
          </div>
        </Popup>
      </WapOnly>
    </div>
  );
}
